'use client'

import { useEffect } from 'react'

/**
 * global-error is de laatste vanglaag en importeert bewust niets uit @streep/ui
 * of next-intl, zodat hij ook rendert als die bundle of de i18n-provider stuk
 * is (de bestaande markup hieronder gebruikt enkel Tailwind). Daarom hier een
 * eigen, compacte
 * ChunkLoadError-detectie i.p.v. de gedeelde useReloadOnChunkError-hook die de
 * route-boundaries gebruiken: een chunk-fout (een client met de vorige build na
 * een deploy) herstellen we met een harde reload, met een sessionStorage-
 * debounce tegen reload-loops. Houd de patronen en de debounce-sleutel/-venster
 * in sync met isChunkLoadError / useReloadOnChunkError in @streep/ui.
 */
function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const name = (error as { name?: string }).name ?? ''
  const message = (error as { message?: string }).message ?? ''
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message)
  )
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    if (isChunkLoadError(error)) {
      try {
        const key = 'streep:chunk-reload-at' // zelfde key als useReloadOnChunkError (bewust geïnlined)
        const last = Number(window.sessionStorage.getItem(key) ?? '0')
        if (Date.now() - last < 10_000) return // net al herladen → niet loopen
        window.sessionStorage.setItem(key, String(Date.now()))
      } catch {
        // geen sessionStorage beschikbaar - reload alsnog uitvoeren
      }
      window.location.reload()
      return
    }
    console.error(error)
  }, [error])

  return (
    <html>
      <body>
        <div className="flex min-h-screen items-center justify-center bg-[#BABDB0] px-4">
          <div className="w-full max-w-md rounded-xl border border-[#CED0C8] bg-white p-8 shadow-sm text-center">
            <h1 className="mb-2 text-2xl font-semibold text-[#1A1A18]">
              Er is iets misgegaan
            </h1>
            <p className="mb-6 text-sm text-[#5C5C58]">
              Probeer het opnieuw. Neem contact op met je STREEP-contactpersoon
              als het probleem aanhoudt.
            </p>
            <button
              type="button"
              onClick={() => reset()}
              className="inline-block rounded-lg bg-[#1A1A18] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#2a2a26]"
            >
              Opnieuw proberen
            </button>
            {/* FO-364 - opaque fout-referentie (Next.js error-digest, staat ook
                in de server-log) zodat een gebruikersmelding herleidbaar is.
                "ref:" is taalneutraal (NL/EN); geen stack, geen PII. */}
            {error.digest ? (
              <p className="mt-4 break-all font-mono text-xs text-[#5C5C58]">
                ref: {error.digest}
              </p>
            ) : null}
          </div>
        </div>
      </body>
    </html>
  )
}
